// FIN-11822 (audit umbrella FIN-11821) — generic React error boundary.
//
// Wraps risky subtrees so an uncaught render exception in any child stays
// scoped to that subtree instead of unmounting parent (e.g. an entire
// drawer). Used by ConfigureDrawer + its tabs after FIN-11819 / FIN-11820
// revealed the production-React default ("uncaught throw → React unmounts
// ancestor tree → drawer disappears") is the wrong failure mode for the
// scenario configuration UX.
//
// Props:
//   - fallback: ReactNode rendered when a child throws.
//   - onError?: (error, errorInfo) => void — optional telemetry hook.
//   - children: protected subtree.
import React from 'react';
import PropTypes from 'prop-types';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    if (typeof this.props.onError === 'function') {
      this.props.onError(error, errorInfo);
    }
    // Log to console even when no onError is supplied so the error is
    // discoverable in browser DevTools instead of swallowed.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  fallback: PropTypes.node.isRequired,
  onError: PropTypes.func,
  children: PropTypes.node
};

ErrorBoundary.defaultProps = {
  onError: undefined,
  children: null
};

export default ErrorBoundary;
